import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "../components/UserAvatar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Shield, Download, Trash2, Loader2, Lock, Eye, Database, Bell as BellIcon, Clock, LogIn, MessageSquare, Bot, FileText, Ban, VolumeX } from "lucide-react";
import api from "../lib/api";

const ACTION_ICONS = {
  "auth.login": LogIn, "auth.register": LogIn, "ai.request": Bot,
  "data.create": MessageSquare, "data.export": Download, "data.erasure": Trash2,
  "privacy.consent_update": Shield, "privacy.data_request": FileText,
};

function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function PrivacySettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [consent, setConsent] = useState({ ai_data_learning: true, public_profile: true, data_sharing: false, marketing_emails: false });
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);

  // Delete account
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Blocked & muted
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [mutedUsers, setMutedUsers] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/privacy/consent");
        if (alive) setConsent(r.data);
      } catch { /* keep defaults */ }
      try { await loadActivity(1); } catch { /* ignore */ }
      try {
        const [b, m] = await Promise.all([api.get("/users/blocked"), api.get("/users/muted")]);
        if (alive) {
          setBlockedUsers(b.data?.users || []);
          setMutedUsers(m.data?.users || []);
        }
      } catch { /* ignore */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const handleUnblock = async (username) => {
    try {
      await api.delete(`/users/${username}/block`);
      setBlockedUsers((prev) => prev.filter((u) => u.username !== username));
      toast.success(`Unblocked @${username}`);
    } catch { toast.error("Failed to unblock"); }
  };

  const handleUnmute = async (username) => {
    try {
      await api.delete(`/users/${username}/mute`);
      setMutedUsers((prev) => prev.filter((u) => u.username !== username));
      toast.success(`Unmuted @${username}`);
    } catch { toast.error("Failed to unmute"); }
  };

  const loadActivity = async (page) => {
    try {
      const r = await api.get(`/privacy/my-activity?page=${page}&limit=20`);
      if (page === 1) setActivity(r.data.entries);
      else setActivity((prev) => [...prev, ...r.data.entries]);
      setActivityTotal(r.data.total);
      setActivityPage(page);
    } catch { /* ignore */ }
  };

  const updateConsent = async (key, value) => {
    // Snapshot the previous state up-front so concurrent toggles or stale
    // closures cannot revert to the wrong values on failure.
    const previous = consent;
    const updated = { ...consent, [key]: value };
    setConsent(updated);
    try {
      await api.put("/privacy/consent", updated);
      toast.success("Privacy preference updated");
    } catch {
      toast.error("Failed to update");
      setConsent(previous);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/privacy/my-data");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `appmi-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported successfully");
    } catch { toast.error("Failed to export data"); }
    finally { setDownloading(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== "DELETE") return;
    setDeleting(true);
    try {
      await api.delete("/privacy/my-data");
      toast.success("Your account and all data have been permanently deleted.");
      logout();
      navigate("/");
    } catch { toast.error("Failed to delete account"); }
    finally { setDeleting(false); }
  };

  const toggles = [
    { key: "ai_data_learning", label: "Allow AI to learn from my conversations", desc: "When enabled, your AI assistants can remember preferences from conversations to personalize responses." },
    { key: "public_profile", label: "Make my profile public", desc: "When enabled, other users can find and view your profile, posts, and AI assistants." },
    { key: "data_sharing", label: "Help improve App-Mi.ai", desc: "When enabled, anonymized usage data helps us improve the platform experience." },
    { key: "marketing_emails", label: "Receive marketing emails", desc: "Get updates about new features, tips, and product announcements." },
  ];

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="privacy-settings-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
        <Shield className="w-4 h-4 text-cyan-400" />
        <h1 className="font-outfit text-base font-semibold text-foreground">Privacy & Settings</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
        {/* Data & Privacy */}
        <section data-testid="consent-section">
          <h2 className="font-outfit text-lg font-medium text-foreground mb-1">Data & Privacy</h2>
          <p className="text-xs text-muted-foreground mb-4">Control how your data is used</p>
          <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
            {toggles.map((t, i) => (
              <div key={t.key} className={`flex items-center justify-between px-5 py-4 ${i > 0 ? "border-t border-border/40" : ""}`}>
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-foreground">{t.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.desc}</p>
                </div>
                <button
                  onClick={() => updateConsent(t.key, !consent[t.key])}
                  className={`w-11 h-6 rounded-full transition-all cursor-pointer border-0 flex-shrink-0 ${consent[t.key] ? "bg-indigo-500" : "bg-muted"}`}
                  data-testid={`toggle-${t.key}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-card shadow-sm transition-transform ${consent[t.key] ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Your Data */}
        <section data-testid="data-section">
          <h2 className="font-outfit text-lg font-medium text-foreground mb-1">Your Data</h2>
          <p className="text-xs text-muted-foreground mb-4">Download or delete your data</p>
          <div className="bg-card border border-border/60 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Download My Data</p>
                <p className="text-xs text-muted-foreground">Export all your data as a JSON file</p>
              </div>
              <Button onClick={handleDownload} disabled={downloading} variant="outline" className="border-border text-foreground/80 bg-card h-9 text-xs px-4 rounded-lg press-down" data-testid="download-data-btn">
                {downloading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />} Download
              </Button>
            </div>
            <div className="border-t border-border/40 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-400">Delete My Account & All Data</p>
                  <p className="text-xs text-muted-foreground">Permanently delete everything. This cannot be undone.</p>
                </div>
                <Button onClick={() => setShowDelete(true)} variant="outline" className="border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/15 h-9 text-xs px-4 rounded-lg" data-testid="delete-account-btn">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Blocked & Muted */}
        <section data-testid="blocked-muted-section">
          <h2 className="font-outfit text-lg font-medium text-foreground mb-1">Blocked & muted</h2>
          <p className="text-xs text-muted-foreground mb-4">People you've blocked or muted</p>
          <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-border/40">
              <Ban className="w-3.5 h-3.5 text-red-400" />
              <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">Blocked</p>
            </div>
            {blockedUsers.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground/70 py-6" data-testid="blocked-empty">You haven't blocked anyone</p>
            ) : (
              <div data-testid="blocked-list">
                {blockedUsers.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-3 px-5 py-3 border-t border-border/40 first:border-t-0">
                    <UserAvatar name={u.display_name} username={u.username} avatarUrl={u.avatar_url} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{u.display_name}</p>
                      <p className="text-xs text-muted-foreground/70">@{u.username}</p>
                    </div>
                    <Button onClick={() => handleUnblock(u.username)} variant="outline" className="border-border text-foreground/80 bg-card h-8 text-xs px-3 rounded-lg press-down" data-testid={`unblock-${u.username}`}>
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 px-5 py-3 border-t border-b border-border/40 bg-white/[0.03]">
              <VolumeX className="w-3.5 h-3.5 text-muted-foreground/70" />
              <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">Muted</p>
            </div>
            {mutedUsers.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground/70 py-6" data-testid="muted-empty">You haven't muted anyone</p>
            ) : (
              <div data-testid="muted-list">
                {mutedUsers.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-3 px-5 py-3 border-t border-border/40 first:border-t-0">
                    <UserAvatar name={u.display_name} username={u.username} avatarUrl={u.avatar_url} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{u.display_name}</p>
                      <p className="text-xs text-muted-foreground/70">@{u.username}</p>
                    </div>
                    <Button onClick={() => handleUnmute(u.username)} variant="outline" className="border-border text-foreground/80 bg-card h-8 text-xs px-3 rounded-lg press-down" data-testid={`unmute-${u.username}`}>
                      Unmute
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Activity Log */}
        <section data-testid="activity-section">
          <h2 className="font-outfit text-lg font-medium text-foreground mb-1">Activity Log</h2>
          <p className="text-xs text-muted-foreground mb-4">Your recent account activity</p>
          <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
            {activity.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground/70 py-8">No activity yet</p>
            ) : (
              activity.map((e, i) => {
                const Icon = ACTION_ICONS[e.action] || Clock;
                return (
                  <div key={e.id} className={`flex items-center gap-3 px-5 py-3 ${i > 0 ? "border-t border-border/40" : ""}`}>
                    <div className="w-7 h-7 rounded-lg bg-muted/40 flex items-center justify-center flex-shrink-0"><Icon className="w-3.5 h-3.5 text-muted-foreground" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground/80">{e.action.replace(/\./g, " → ")}</p>
                      {e.details?.pii_detected !== undefined && <p className="text-[10px] text-muted-foreground/70">PII detected: {e.details.pii_detected}</p>}
                    </div>
                    <span className="text-[10px] text-muted-foreground/70 flex-shrink-0">{formatRelativeTime(e.timestamp)}</span>
                  </div>
                );
              })
            )}
            {activity.length < activityTotal && (
              <button onClick={() => loadActivity(activityPage + 1)} className="w-full py-3 text-xs text-cyan-400 font-medium bg-transparent border-0 border-t border-border/40 cursor-pointer hover:bg-white/5" data-testid="load-more-activity">Load more</button>
            )}
          </div>
        </section>

        {/* Security settings link (2FA lives on its own page) */}
        <section data-testid="security-link-section">
          <Link to="/settings/security" className="flex items-center justify-between bg-card border border-border/60 rounded-xl px-5 py-4 no-underline hover:border-cyan-500/30 transition-colors" data-testid="security-settings-link">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-cyan-500/10 flex items-center justify-center"><Lock className="w-3.5 h-3.5 text-cyan-400" /></div>
              <div>
                <p className="text-sm font-medium text-foreground">Security settings</p>
                <p className="text-xs text-muted-foreground">Two-factor authentication & sign-in protection</p>
              </div>
            </div>
            <span className="text-sm text-cyan-400 font-medium">&rarr;</span>
          </Link>
        </section>

        {/* Links */}
        <div className="flex gap-4 text-xs">
          <Link to="/privacy-policy" className="text-cyan-400 no-underline hover:text-cyan-300">Privacy Policy</Link>
          <Link to="/terms" className="text-cyan-400 no-underline hover:text-cyan-300">Terms of Service</Link>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDelete(false)} data-testid="delete-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-red-500/10 flex items-center justify-center"><Trash2 className="w-6 h-6 text-red-500" /></div>
            <h2 className="font-outfit text-lg font-semibold text-foreground text-center mb-2">Delete Your Account?</h2>
            <p className="text-sm text-muted-foreground text-center mb-4">This action is <strong className="text-red-400">permanent and cannot be undone</strong>. All your data, AI assistants, posts, conversations, and profile will be permanently deleted.</p>
            <p className="text-xs text-muted-foreground mb-2">Type <strong>DELETE</strong> to confirm:</p>
            <input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 mb-4" data-testid="delete-confirm-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowDelete(false)} className="flex-1 px-4 py-2.5 text-sm text-muted-foreground bg-muted/70 rounded-lg border-0 cursor-pointer hover:bg-white/10">Cancel</button>
              <Button onClick={handleDelete} disabled={deleteConfirm !== "DELETE" || deleting} className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg disabled:opacity-40" data-testid="confirm-delete-btn">
                {deleting ? "Deleting..." : "Delete Everything"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
